"use client";

import { createApi } from "@reduxjs/toolkit/query/react";

import baseQuery from "@/libs/shared/api/baseQuery";
import {
  AccountModel,
  ProductModel,
  TransactionModel,
} from "@/libs/products/redux/products.types";
import { GetPayloadModel, GetResponseModel } from "@/libs/shared/api/api.types";
import { GroupModel } from "@/libs/customers/components/CustomerSummary/Groups/Groups.types";

const tagTypes = [
  "Account" as const,
  "Product" as const,
  "Products" as const,
  "Transactions" as const,
  "Groups" as const,
];

export const invalidateProducts = (tags: typeof tagTypes) => {
  return (_: any, api: any) => {
    return api.queryFulfilled.then(() => {
      tags.forEach((tag) => {
        api.dispatch(productsApi.util.invalidateTags([tag]));
      });
    });
  };
};

// Define a service using a base URL and expected endpoints
export const productsApi = createApi({
  reducerPath: "productsApi",
  baseQuery,
  tagTypes,
  endpoints: (builder) => ({
    getAccount: builder.query<AccountModel, void>({
      query: () => ({
        url: `/pt/revoo_pay/account`,
      }),
      providesTags: ["Account"],
    }),
    startAccount: builder.mutation<string, void>({
      query: () => ({
        url: `/pt/revoo_pay/account/start`,
        method: "POST",
      }),
      onQueryStarted: invalidateProducts(["Account"]),
    }),
    getProducts: builder.query<ProductModel[], void>({
      query: () => ({
        url: `/pt/revoo_pay/products`,
      }),
      providesTags: ["Products"],
    }),
    getGroups: builder.query<
      GetResponseModel<GroupModel>,
      Partial<GetPayloadModel>
    >({
      query: ({ page, perPage }) => ({
        url: `pt/groups?per_page=${perPage}&page=${page}`,
      }),
      providesTags: ["Groups"],
    }),
    getProduct: builder.query<ProductModel, { productId: string }>({
      query: ({ productId }: { productId: string }) => ({
        url: `/pt/revoo_pay/products?product_id=${productId}`,
      }),
      providesTags: ["Product"],
    }),
    createProduct: builder.mutation<ProductModel, { body: ProductModel }>({
      query: ({ body }: { productId: string; body: ProductModel }) => ({
        url: `/pt/revoo_pay/products`,
        method: "POST",
        body,
      }),
      onQueryStarted: invalidateProducts(["Product", "Products"]),
    }),
    editProduct: builder.mutation<
      ProductModel,
      { productId: string; body: ProductModel }
    >({
      query: ({
        productId,
        body,
      }: {
        productId: string;
        body: ProductModel;
      }) => ({
        url: `/pt/revoo_pay/products/${productId}`,
        method: "PUT",
        body,
      }),
      onQueryStarted: invalidateProducts(["Product", "Products"]),
    }),
    deleteProduct: builder.mutation<ProductModel, { productId: string }>({
      query: ({ productId }: { productId: string }) => ({
        url: `/pt/revoo_pay/products/${productId}`,
        method: "DELETE",
      }),
      onQueryStarted: invalidateProducts(["Products"]),
    }),
    notifyProduct: builder.mutation<ProductModel, { productId: string }>({
      query: ({ productId }: { productId: string }) => ({
        url: `/pt/revoo_pay/products/${productId}/notify`,
        method: "POST",
      }),
    }),
    updateProductsRank: builder.mutation<
      ProductModel[],
      { body: { sortedList: number[] } }
    >({
      query: ({ body }) => ({
        url: `/pt/revoo_pay/products/rank`,
        method: "POST",
        body,
      }),
      onQueryStarted: invalidateProducts(["Products"]),
    }),
    getTransactions: builder.query<TransactionModel[], void>({
      query: () => ({
        url: `/pt/revoo_pay/transactions`,
      }),
      providesTags: ["Transactions"],
    }),
    exportTransactions: builder.mutation<string, void>({
      query: () => ({
        url: `/pt/revoo_pay/transactions/export`,
        method: "GET",
      }),
    }),
    fetchTransaction: builder.mutation<void, void>({
      query: () => ({
        url: `/pt/revoo_pay/transactions/fetch`,
        method: "GET",
      }),
      onQueryStarted: invalidateProducts(["Transactions"]),
    }),
  }),
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  useGetAccountQuery,
  useGetProductQuery,
  useGetProductsQuery,
  useGetGroupsQuery,
  useGetTransactionsQuery,
  useCreateProductMutation,
  useEditProductMutation,
  useDeleteProductMutation,
  useNotifyProductMutation,
  useUpdateProductsRankMutation,
  useStartAccountMutation,
  useExportTransactionsMutation,
  useFetchTransactionMutation,
} = productsApi;
